import { audio } from '../systems/audio/AudioSystem.js';
import { RepriseClient } from '../systems/net/RepriseClient.js';
import { FRAGMENTS, fragmentLabel, fragmentRarityColor, getFragment } from '../data/fragments.js';
import { summarizeWorldBuffs } from '../data/repriseWorld.js';
import { REPRISE_TUNING } from '../data/repriseTuning.js';

// The 6x4 world grid — deliberately the same shape as The Unmade King's
// Unmade Ground (ARCHIVE.md §7), which at season start is entirely void.
const GRID_W = 6;
const GRID_H = 4;
const PULL_UNIT_COST = REPRISE_TUNING.pullUnitCost; // display mirror; server (reprise_config) is authoritative

function pct(resonance, required) {
  if (!(required > 0)) return 0;
  return Math.max(0, Math.min(1, resonance / required));
}

/**
 * Reprise world hub (REPRISE.md phases R0–R1): the 6x4 district map, the
 * player's salvage inventory, a server-rolled fragment pull, and delivery of
 * fragments into the open front. Resonance still arrives at run-end
 * (RunEndOverlay), not here. All network I/O is offline-tolerant
 * (RepriseClient) — no season / no account / an unreachable server degrade to
 * empty states, never a crash.
 */
export class RepriseMenu {
  constructor(root, save, { onBack, onBarter, onChoirs }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.onBarter = onBarter;
    this.onChoirs = onChoirs;
    this.world = null;
    this.frags = {};
    this.busy = false;
    this._flash = null; // one-shot subtitle message that survives the next render
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay reprise-overlay';
    this._renderShell();
    this.root.appendChild(this.el);
    this._load();
  }

  get signedIn() {
    return Boolean(this.save.accountUsername);
  }

  _renderShell() {
    const kicker = document.createElement('div');
    kicker.className = 'menu-kicker';
    kicker.textContent = 'REPRISE';
    this.el.appendChild(kicker);

    this.heading = document.createElement('div');
    this.heading.className = 'menu-title';
    this.heading.textContent = 'REBUILDING MELODIA';
    this.el.appendChild(this.heading);

    this.subtitle = document.createElement('div');
    this.subtitle.className = 'menu-subtitle';
    this.subtitle.textContent = 'Loading the world…';
    this.el.appendChild(this.subtitle);

    this.body = document.createElement('div');
    this.body.className = 'reprise-body';
    this.el.appendChild(this.body);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  async _load() {
    // World is public; fragments need a session (returns empty for guests).
    // buffState resolves which restored districts this player has amplified.
    const [world, frags, buffs, finale] = await Promise.all([
      RepriseClient.getWorld(),
      this.signedIn ? RepriseClient.getFragments() : Promise.resolve({ ok: true, fragments: {} }),
      RepriseClient.getBuffState(),
      RepriseClient.getFinale(),
    ]);
    if (!this.el.isConnected) return; // left the screen mid-request
    this.world = world;
    this.frags = frags.ok ? frags.fragments : {};
    this.buffState = buffs;
    this.finale = finale.ok ? finale.finale : null;
    this._maybeGrantMemento();
    this._render();
  }

  // R5 memento (REPRISE.md §7): once the season's King has fallen, anyone who
  // helped rebuild its world earns the Rebuilder's Mark. unlockCosmetic is
  // idempotent, so re-granting on every visit is harmless.
  _maybeGrantMemento() {
    if (!this.signedIn || !this.finale?.defeated_at) return;
    const helped = (this.buffState?.contributedIds?.length ?? 0) > 0;
    if (helped && !this.save.ownsCosmetic('skin-rebuilder')) {
      this.save.unlockCosmetic('skin-rebuilder');
      this._flash = "The King has fallen — you've earned the Rebuilder's Mark (a skin).";
    }
  }

  _render() {
    if (!this.world?.ok) {
      this.subtitle.textContent = 'The world sleeps';
      this.body.innerHTML = '';
      const empty = document.createElement('div');
      empty.className = 'reprise-empty';
      empty.textContent = 'No reprise has begun. When the next season opens, the unmade ground will be yours to restore.';
      this.body.appendChild(empty);
      return;
    }

    const { season, districts } = this.world;
    const restored = districts.filter((d) => d.state === 'restored').length;
    const front = districts.find((d) => d.state === 'open') || null;
    // A pending flash (a pull/deliver outcome) wins this one render, then the
    // subtitle reverts to the season readout.
    this.subtitle.textContent = this._flash
      || `Season ${season.season_index} · ${restored} of ${districts.length} districts restored`;
    this._flash = null;

    this.body.innerHTML = '';
    // R5: during (or after) a finale the King panel leads; the map below is
    // fully lit. Otherwise the open front leads as before.
    if (this.finale) this.body.appendChild(this._renderFinale());
    else this.body.appendChild(this._renderFront(front));
    this.body.appendChild(this._renderGrid(districts));
    const buffs = this._renderBuffs();
    if (buffs) this.body.appendChild(buffs);
    this.body.appendChild(this._renderSalvage());
  }

  _renderFinale() {
    const f = this.finale;
    const panel = document.createElement('div');
    panel.className = 'reprise-finale';
    if (f.defeated_at) {
      panel.innerHTML = `
        <div class="reprise-finale-label">THE UNMADE KING HAS FALLEN</div>
        <div class="reprise-front-name">Melodia stands again</div>
        <div class="reprise-front-hint">The world is whole. The next reprise will re-void the map into a new shape.</div>
      `;
      return panel;
    }
    const filled = f.boss_max_hp > 0 ? f.boss_hp / f.boss_max_hp : 0;
    panel.innerHTML = `
      <div class="reprise-finale-label">THE UNMADE KING HAS REFORMED</div>
      <div class="reprise-front-name">Every district restored — now fell him, together.</div>
      <div class="reprise-bar reprise-bar-king"><div class="reprise-bar-fill" style="width:${(filled * 100).toFixed(1)}%"></div></div>
      <div class="reprise-front-nums">${Math.floor(f.boss_hp).toLocaleString()} / ${Math.floor(f.boss_max_hp).toLocaleString()} HP · ${f.contributor_count} rebuilders</div>
      <div class="reprise-front-hint">Every run now strikes the King. Finish one to chip his shared health.</div>
    `;
    return panel;
  }

  _renderBuffs() {
    const state = this.buffState;
    if (!state || state.restoredIds.length === 0) return null;
    const { lines, anyAmplified } = summarizeWorldBuffs(state.restoredIds, state.contributedIds);
    if (lines.length === 0) return null;
    const panel = document.createElement('div');
    panel.className = 'reprise-buffs';
    const head = document.createElement('div');
    head.className = 'reprise-salvage-title';
    head.textContent = 'THE RESTORED WORLD GRANTS';
    panel.appendChild(head);
    const list = document.createElement('div');
    list.className = 'reprise-buff-list';
    for (const line of lines) {
      const chip = document.createElement('span');
      chip.className = 'reprise-buff-chip';
      chip.textContent = line;
      list.appendChild(chip);
    }
    panel.appendChild(list);
    const note = document.createElement('div');
    note.className = 'reprise-front-hint';
    note.textContent = anyAmplified
      ? 'Amplified: districts you helped restore give you their stronger tier.'
      : 'Contribute to a district to unlock its stronger, amplified tier for you.';
    panel.appendChild(note);
    return panel;
  }

  _renderFront(front) {
    const panel = document.createElement('div');
    panel.className = 'reprise-front';
    if (!front) {
      panel.innerHTML = '<div class="reprise-front-label">The season\'s work is done — the King stirs.</div>';
      return panel;
    }

    const filled = pct(front.resonance, front.resonance_required);
    const reqs = front.fragment_reqs || {};
    const delivered = front.fragments_delivered || {};

    const header = document.createElement('div');
    header.innerHTML = `
      <div class="reprise-front-label">RESTORING · ${front.quarter}</div>
      <div class="reprise-front-name">${front.label}</div>
      <div class="reprise-bar"><div class="reprise-bar-fill" style="width:${(filled * 100).toFixed(1)}%"></div></div>
      <div class="reprise-front-nums">${Math.floor(front.resonance).toLocaleString()} / ${Math.floor(front.resonance_required).toLocaleString()} Resonance</div>
    `;
    panel.appendChild(header);

    // Fragment requirements: delivered / needed per type, plus what the
    // player currently holds, so the shortfall (and thus the reason to pull
    // or trade) is legible at a glance.
    const reqIds = Object.keys(reqs);
    if (reqIds.length > 0) {
      const reqWrap = document.createElement('div');
      reqWrap.className = 'reprise-reqs';
      let canDeliver = false;
      for (const id of reqIds) {
        const need = reqs[id];
        const have = delivered[id] || 0;
        const owned = this.frags[id]?.total || 0;
        const met = have >= need;
        if (!met && owned > 0) canDeliver = true;
        const chip = document.createElement('div');
        chip.className = `reprise-req${met ? ' reprise-req-met' : ''}`;
        chip.style.setProperty('--frag-color', fragmentRarityColor(id));
        chip.innerHTML = `
          <span class="reprise-req-glyph">${getFragment(id)?.glyph ?? '◇'}</span>
          <span class="reprise-req-name">${fragmentLabel(id)}</span>
          <span class="reprise-req-count">${have} / ${need}</span>
          <span class="reprise-req-owned">you: ${owned}</span>
        `;
        reqWrap.appendChild(chip);
      }
      panel.appendChild(reqWrap);

      if (this.signedIn) {
        const deliver = document.createElement('button');
        deliver.type = 'button';
        deliver.className = 'menu-button menu-button-primary reprise-deliver';
        deliver.textContent = 'Deliver Salvage';
        deliver.disabled = this.busy || !canDeliver;
        deliver.addEventListener('click', () => this._deliver(front));
        panel.appendChild(deliver);
        if (!canDeliver) {
          const hint = document.createElement('div');
          hint.className = 'reprise-front-hint';
          hint.textContent = filled >= 1
            ? 'This district needs salvage you don\'t hold yet — pull below, or trade for it.'
            : 'Finish runs to fill the Resonance bar; pull and deliver the salvage it needs.';
          panel.appendChild(hint);
        }
      }
    } else {
      const hint = document.createElement('div');
      hint.className = 'reprise-front-hint';
      hint.textContent = 'Finish a run to pour your Resonance into the world.';
      panel.appendChild(hint);
    }

    return panel;
  }

  _renderGrid(districts) {
    const byCell = new Map(districts.map((d) => [`${d.cell_x},${d.cell_y}`, d]));
    const grid = document.createElement('div');
    grid.className = 'reprise-grid';
    grid.style.setProperty('--grid-w', String(GRID_W));
    for (let y = 0; y < GRID_H; y += 1) {
      for (let x = 0; x < GRID_W; x += 1) {
        const d = byCell.get(`${x},${y}`);
        const cell = document.createElement('div');
        cell.className = 'reprise-cell';
        if (!d) { cell.classList.add('reprise-cell-void'); grid.appendChild(cell); continue; }
        cell.classList.add(`reprise-cell-${d.state}`);
        const name = document.createElement('span');
        name.className = 'reprise-cell-name';
        name.textContent = d.label;
        cell.appendChild(name);
        if (d.state === 'open') {
          const bar = document.createElement('div');
          bar.className = 'reprise-cell-bar';
          bar.innerHTML = `<div class="reprise-cell-bar-fill" style="width:${(pct(d.resonance, d.resonance_required) * 100).toFixed(0)}%"></div>`;
          cell.appendChild(bar);
        }
        cell.title = `${d.label} · ${d.quarter} · ${d.state}`;
        grid.appendChild(cell);
      }
    }
    return grid;
  }

  _renderSalvage() {
    const panel = document.createElement('div');
    panel.className = 'reprise-salvage';

    const head = document.createElement('div');
    head.className = 'reprise-salvage-head';
    head.innerHTML = `<span class="reprise-salvage-title">SALVAGE</span>`;
    panel.appendChild(head);

    if (!this.signedIn) {
      const gate = document.createElement('div');
      gate.className = 'reprise-front-hint';
      gate.textContent = 'Sign in to pull salvage and deliver it into the world.';
      panel.appendChild(gate);
      return panel;
    }

    // Inventory: every known fragment type, with the player's count. Zero-
    // count types are shown dimmed so the full salvage set reads as a goal.
    const inv = document.createElement('div');
    inv.className = 'reprise-inv';
    for (const frag of FRAGMENTS) {
      const held = this.frags[frag.id];
      const total = held?.total || 0;
      const chip = document.createElement('div');
      chip.className = `reprise-frag${total === 0 ? ' reprise-frag-empty' : ''}`;
      chip.style.setProperty('--frag-color', fragmentRarityColor(frag.id));
      // tradeable count is surfaced now (it's what R3 trading will move) so
      // the bound-vs-spare distinction is visible before trading ships.
      chip.title = `${frag.label} · ${frag.quarter}${held ? ` · ${held.bound} bound, ${held.tradeable} spare` : ''}`;
      chip.innerHTML = `
        <span class="reprise-frag-glyph">${frag.glyph}</span>
        <span class="reprise-frag-name">${frag.label}</span>
        <span class="reprise-frag-count">${total}</span>
      `;
      inv.appendChild(chip);
    }
    panel.appendChild(inv);

    const reveal = document.createElement('div');
    reveal.className = 'reprise-pull-reveal';
    panel.appendChild(reveal);
    this._revealEl = reveal;

    const actions = document.createElement('div');
    actions.className = 'reprise-pull-actions';
    const single = this._pullButton('Pull ×1', 1);
    const ten = this._pullButton('Pull ×10', 10);
    actions.appendChild(single);
    actions.appendChild(ten);
    panel.appendChild(actions);

    const cost = document.createElement('div');
    cost.className = 'reprise-front-hint';
    cost.textContent = `Shards: ${this.save.shards} · ${PULL_UNIT_COST} per pull`;
    panel.appendChild(cost);

    const nav = document.createElement('div');
    nav.className = 'reprise-pull-actions';
    if (this.onBarter) {
      const barter = document.createElement('button');
      barter.type = 'button';
      barter.className = 'menu-button reprise-barter-btn';
      barter.textContent = 'Barter →';
      barter.addEventListener('click', () => { audio.playSfx('click'); this.onBarter(); });
      nav.appendChild(barter);
    }
    if (this.onChoirs) {
      const choirs = document.createElement('button');
      choirs.type = 'button';
      choirs.className = 'menu-button reprise-barter-btn';
      choirs.textContent = 'Choirs →';
      choirs.addEventListener('click', () => { audio.playSfx('click'); this.onChoirs(); });
      nav.appendChild(choirs);
    }
    if (nav.children.length > 0) panel.appendChild(nav);

    return panel;
  }

  _pullButton(label, count) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'menu-button reprise-pull-btn';
    btn.textContent = label;
    btn.disabled = this.busy || this.save.shards < PULL_UNIT_COST * count;
    btn.addEventListener('click', () => this._pull(count));
    return btn;
  }

  async _pull(count) {
    if (this.busy) return;
    this.busy = true;
    audio.playSfx('click');
    const result = await RepriseClient.pull({ count });
    if (!this.el.isConnected) return;
    this.busy = false;
    if (!result.ok) {
      this._flash = result.error === 'offline'
        ? 'Salvage is unreachable right now.'
        : 'Pull failed — check your Shards and try again.';
      this._render();
      return;
    }
    // The RPC already debited Shards server-side; adopt its post-debit
    // number so a later CloudSync push can't overwrite the decrement.
    this.save.syncShardsFromServer(result.shards_remaining);
    audio.playSfx('levelup'); // the reveal chime the pet summon reel uses too
    // Refresh inventory and re-render, then show what was rolled.
    const frags = await RepriseClient.getFragments();
    if (!this.el.isConnected) return;
    this.frags = frags.ok ? frags.fragments : this.frags;
    this._render();
    this._showReveal(result.rolled || []);
  }

  _showReveal(rolled) {
    if (!this._revealEl) return;
    this._revealEl.innerHTML = '';
    for (const id of rolled) {
      const chip = document.createElement('span');
      chip.className = 'reprise-reveal-chip';
      chip.style.setProperty('--frag-color', fragmentRarityColor(id));
      chip.textContent = `${getFragment(id)?.glyph ?? '◇'} ${fragmentLabel(id)}`;
      this._revealEl.appendChild(chip);
    }
  }

  async _deliver(front) {
    if (this.busy) return;
    this.busy = true;
    audio.playSfx('click');
    // Optimistically offer everything owned of each still-needed type; the
    // server clamps to the remaining need, so over-offering is safe.
    const reqs = front.fragment_reqs || {};
    const delivered = front.fragments_delivered || {};
    const deliveries = {};
    for (const id of Object.keys(reqs)) {
      const remaining = reqs[id] - (delivered[id] || 0);
      const owned = this.frags[id]?.total || 0;
      const give = Math.min(remaining, owned);
      if (give > 0) deliveries[id] = give;
    }
    const result = await RepriseClient.deliver({ districtId: front.district_id, deliveries });
    if (!this.el.isConnected) return;
    this.busy = false;
    if (!result.ok) {
      this._flash = 'Delivery failed — the front may have moved.';
    } else if (result.restored) {
      this._flash = `${front.label} restored! The world grows.`;
      audio.playSfx('victory');
    } else if (Object.keys(result.applied || {}).length > 0) {
      this._flash = `Salvage delivered to ${front.label}.`;
    }
    await this._load(); // pull fresh world (front may have changed) + inventory
  }

  destroy() {
    this.el.remove();
  }
}
